'use client';

import { t } from '@lingui/macro';
import { Checkbox, CircularProgress, TextField } from '@mui/material';
import { Autocomplete } from '@prismo-io/design-system';
import { useEffect, useState } from 'react';
import { type Control, Controller, type FieldValues, type Path } from 'react-hook-form';
import useDebounceValue from '../../../helpers/use-debounce';
import { useAppellationsAutocomplete } from './use-appellations-autocomplete';
export type AppellationsAutocompleteInputProps<F extends FieldValues = FieldValues> = {
  name: Path<F>;
  control: Control<F>;
  required?: boolean;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  disableCloseOnSelect?: boolean;
};
export function AppellationsAutocompleteInput<TFieldValues extends FieldValues>(props: AppellationsAutocompleteInputProps<TFieldValues>) {
  const {
    name,
    control,
    required = false,
    label = undefined,
    helperText = undefined,
    disabled = false,
    disableCloseOnSelect = false
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [inputTerm, setInputTerm] = useState<string | null>(null);
  const {
    data: options = [],
    isLoading
  } = useAppellationsAutocomplete({
    isOpen,
    term: inputTerm
  });
  const isLoadingDebounced = useDebounceValue(isLoading, 400);
  return <Controller name={name} control={control} render={({
    field: {
      onChange,
      onBlur,
      value
    },
    fieldState: {
      error
    }
  }) => {
    return <Autocomplete open={isOpen} filterOptions={x => x} onOpen={() => setIsOpen(true)} onClose={() => setIsOpen(false)} value={value} loading={isLoadingDebounced} loadingText={t`Recherche des appellations`} noOptionsText={t`Aucune appellation trouvée`} disabled={disabled} multiple options={options} disableCloseOnSelect={disableCloseOnSelect} onChange={(event, value, reason, details) => {
      onChange(value);
    }} onInputChange={(event, newValue, reason) => {
      if (reason === 'input' || !disableCloseOnSelect) {
        setInputTerm(newValue);
      }
    }} onBlur={event => {
      onBlur();
    }} isOptionEqualToValue={(option, value) => {
      return value ? option?.code === (value?.code ?? value) : false;
    }} getOptionLabel={opt => opt.name} renderOption={(props, option, {
      selected
    }) => <li {...props}>
                <Checkbox size="small" style={{
        marginRight: 8
      }} checked={selected} />
                {option.name}
              </li>} renderInput={params => <TextField label={label} {...params} error={!!error} InputLabelProps={{
      ...params.InputLabelProps,
      required
    }} InputProps={{
      ...params.InputProps,
      endAdornment: <>
                      {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
    }} inputProps={{
      ...params.inputProps
    }} helperText={error ? error.message : helperText} />} />;
  }} data-sentry-element="Controller" data-sentry-component="AppellationsAutocompleteInput" data-sentry-source-file="appellations-autocomplete.tsx" />;
}